/**
 * BillDrawer — right-anchored MUI Drawer for one anomalous bill cycle.
 *
 * The document behind a flagged cycle: `cycle.document.kind` is classified
 * BFF-side, at EMIT time, from the billed period's embedded `raw_file` — so
 * the FE already knows pdf/json/none per cycle with no fetch. `kind ===
 * "none"` (no billed period, or a billed period with no raw file) renders
 * metered-data-only immediately, with NO call to `useGetBillDocument` at
 * all. For "pdf"/"json", `useGetBillDocument` still resolves the drawer
 * endpoint's fresh presigned `download_url` (json: the proxied `content`)
 * lazily, on drawer-open — this component renders purely by the FETCHED
 * `doc.kind`/fields at that point; it does no reconciliation math of its own.
 *
 * Above the document panel, up to three independent panels render keyed on
 * the cycle's own fields — not mutually exclusive, a cycle can be both a
 * billing mismatch AND over-expected:
 *   - `mismatch`      → billed-vs-metered reconciliation line.
 *   - `over_expected` → actual-vs-expected CYCLE TOTALS (usage, plus $ when
 *                       a tariff resolved on both sides).
 *   - `basis`         → gas/water seasonal baseline ("vs typical …"); the
 *                       per-day `baseline_median` is scaled by `days` first
 *                       (unlike electric's already-cycle-total `expected`).
 *
 * Copy contract (historical framing, matching the homepage ACT cards): every
 * $/usage figure here is OBSERVED, past tense — never "savings", "projected",
 * or "forecast".
 */
import type React from "react"

import { useFormatCurrency } from "@/formatters/use-format-currency"
import { HttpError } from "@/utils/api"

import {
  CloseRounded,
  ErrorOutlineRounded,
  ReceiptLongRounded,
  WarningAmberRounded,
} from "@mui/icons-material"
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material"
import { alpha, useTheme } from "@mui/material/styles"

import { useGetBillDocument } from "../../data/use-get-bill-document"
import type { AnomalousCycle } from "./variance.types"

/** A compact label/value stat used across the drawer's detail panels. */
const Stat: React.FC<{ label: string; value: string; color?: string }> = ({
  label,
  value,
  color = "text.primary",
}) => (
  <Stack spacing={0.25}>
    <Typography color="text.secondary" variant="dataLabel">
      {label}
    </Typography>
    <Typography sx={{ fontWeight: "bold", color }} variant="body2">
      {value}
    </Typography>
  </Stack>
)

export interface BillDrawerProps {
  month: AnomalousCycle | null
  open: boolean
  onClose: () => void
}

const dateLabel = (iso: string, withYear: boolean): string =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(withYear ? { year: "numeric" as const } : {}),
    timeZone: "UTC",
  })

const formatPeriod = (period: { start: string; end: string }): string =>
  `${dateLabel(period.start, false)} – ${dateLabel(period.end, true)}`

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v)

/** Human cell text: primitives as-is, nested structures stringified, empties as an em dash. */
const cellText = (v: unknown): string => {
  if (v === null || v === undefined || v === "") {
    return "—"
  }
  if (typeof v === "string") {
    return v
  }
  if (
    typeof v === "number" ||
    typeof v === "boolean" ||
    typeof v === "bigint"
  ) {
    return v.toString()
  }
  return JSON.stringify(v)
}

type ParsedSource =
  | { mode: "records"; records: Record<string, unknown>[] }
  | { mode: "raw"; text: string }

/**
 * Shape the proxied source for display in a NARROW drawer: a uniform array of
 * objects, or a single object, becomes a list of records (each rendered as a
 * stacked label/value card — no wide table, so no horizontal scroll). Anything
 * else (or unparseable / null) → raw text.
 */
const parseSource = (content: string | null): ParsedSource => {
  if (content == null) {
    return { mode: "raw", text: "Could not load the source document." }
  }
  let data: unknown
  try {
    data = JSON.parse(content)
  } catch {
    return { mode: "raw", text: content }
  }
  if (Array.isArray(data) && data.length > 0 && data.every(isPlainObject)) {
    return { mode: "records", records: data }
  }
  if (isPlainObject(data)) {
    return { mode: "records", records: [data] }
  }
  return { mode: "raw", text: JSON.stringify(data, null, 2) }
}

const RecordCard: React.FC<{
  record: Record<string, unknown>
  label?: string
}> = ({ record, label }) => (
  <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1.5 }}>
    {label != null && (
      <Typography
        color="text.secondary"
        sx={{ display: "block", mb: 0.5 }}
        variant="dataLabel"
      >
        {label}
      </Typography>
    )}
    <Stack spacing={0.5}>
      {Object.entries(record).map(([key, value]) => (
        <Stack
          key={key}
          direction="row"
          spacing={2}
          sx={{ justifyContent: "space-between" }}
        >
          <Typography
            color="text.secondary"
            sx={{ flexShrink: 0 }}
            variant="dataLabel"
          >
            {key}
          </Typography>
          <Typography
            sx={{ textAlign: "right", wordBreak: "break-word" }}
            variant="body2"
          >
            {cellText(value)}
          </Typography>
        </Stack>
      ))}
    </Stack>
  </Box>
)

/**
 * Renders the JSON bill source the BFF proxied inline (`content`). The FE never
 * fetches the presigned S3 URL itself — that's CORS-blocked — so the drawer
 * endpoint fetches the bytes server-side and hands them here. `content` is null
 * when that server-side fetch failed. Records render as stacked label/value
 * cards so the narrow drawer never scrolls horizontally.
 */
const JsonSourceView: React.FC<{ content: string | null }> = ({ content }) => {
  const parsed = parseSource(content)

  return (
    <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
      <Typography
        color="text.secondary"
        sx={{ display: "block", mb: 1 }}
        variant="dataLabel"
      >
        Source data (no bill image)
      </Typography>

      {parsed.mode === "records" ? (
        <Stack spacing={1.5}>
          {parsed.records.map((record, i) => (
            <RecordCard
              /* eslint-disable-next-line @eslint-react/no-array-index-key -- static, never-reordered source records */
              key={i}
              label={parsed.records.length > 1 ? `Reading ${i + 1}` : undefined}
              record={record}
            />
          ))}
        </Stack>
      ) : (
        <Box
          component="pre"
          sx={{ m: 0, fontSize: 12, whiteSpace: "pre-wrap" }}
        >
          {parsed.text}
        </Box>
      )}
    </Box>
  )
}

/** Typical cycle-total usage for the metered-only summary: electric's
 * `expected` is already a cycle total; gas/water's `baseline_median` is a
 * per-day rate, so it's scaled by `days` first. Null when neither resolved. */
const typicalCycleTotal = (cycle: AnomalousCycle): number | null => {
  if (cycle.basis != null && cycle.baseline_median != null) {
    return cycle.baseline_median * cycle.days
  }
  return cycle.expected ?? null
}

const MeteredOnlyPanel: React.FC<{ cycle: AnomalousCycle }> = ({ cycle }) => {
  const theme = useTheme()
  const period = cycle.document.service_period ?? cycle.service_period
  const typical = typicalCycleTotal(cycle)

  return (
    <Stack spacing={2} sx={{ flex: 1, p: 3, bgcolor: theme.palette.grey[25] }}>
      <Typography sx={{ fontWeight: "bold" }} variant="body2">
        Metered data only
      </Typography>
      <Stack direction="row" spacing={3}>
        <Stat
          label="Usage"
          value={`${cycle.usage.toLocaleString()} ${cycle.usage_unit}`}
        />
        <Stat
          label="Typical"
          value={
            typical != null
              ? `${Math.round(typical).toLocaleString()} ${cycle.usage_unit}`
              : "—"
          }
        />
        <Stat label="Period" value={formatPeriod(period)} />
      </Stack>
    </Stack>
  )
}

/** Loading / unauthorized / error / pdf / json / metered-only — the document states. */
const DocumentPanel: React.FC<{ cycle: AnomalousCycle }> = ({ cycle }) => {
  const { bill_id: billId, kind } = cycle.document
  // kind is pre-known from the emitted document (classified BFF-side from
  // the billed period's embedded raw_file) — a "none" cycle (no billed
  // period, or a billed period with no raw file) needs no fetch at all.
  const {
    data: doc,
    error,
    isLoading,
  } = useGetBillDocument(kind === "none" ? null : billId)

  if (kind === "none") {
    return <MeteredOnlyPanel cycle={cycle} />
  }

  if (isLoading) {
    return (
      <Stack
        sx={{ alignItems: "center", justifyContent: "center", flex: 1, py: 8 }}
      >
        <CircularProgress size={28} />
      </Stack>
    )
  }

  if (error) {
    const isUnauthorized = error instanceof HttpError && error.status === 403
    return (
      <Stack
        spacing={1}
        sx={{ alignItems: "center", justifyContent: "center", flex: 1, py: 8 }}
      >
        <ErrorOutlineRounded
          color={isUnauthorized ? "warning" : "error"}
          sx={{ fontSize: 28 }}
        />
        <Typography color="text.secondary" variant="body2">
          {isUnauthorized ? "Access restricted" : "Couldn't load bill"}
        </Typography>
      </Stack>
    )
  }

  if (doc?.kind === "pdf" && doc.download_url) {
    return (
      <Box
        component="iframe"
        sx={{ border: 0, flex: 1, width: "100%" }}
        src={doc.download_url}
        title="Bill document"
      />
    )
  }

  if (doc?.kind === "json") {
    return <JsonSourceView content={doc.content ?? null} />
  }

  // The fetched classification came back "none" after all (e.g. the
  // embedded raw_file predicted a doc but the fresh resolve found none) —
  // land on the same metered-data-only summary as the pre-known "none" case.
  return <MeteredOnlyPanel cycle={cycle} />
}

/**
 * AMI (interval-metered) vs billed usage reconciliation — the exact values +
 * difference behind the row's "Metered ≠ billed" flag. Renders only when the
 * cycle carries the mismatch flag and both comparands.
 */
const MismatchPanel: React.FC<{ cycle: AnomalousCycle }> = ({ cycle }) => {
  const theme = useTheme()
  if (
    !cycle.mismatch ||
    cycle.billed_usage == null ||
    cycle.derived_usage == null
  ) {
    return null
  }
  const unit = cycle.usage_unit
  const pct = cycle.mismatch_pct != null ? Math.round(cycle.mismatch_pct) : null

  return (
    <Box
      sx={{
        px: 3,
        py: 2,
        borderBottom: `1px solid ${theme.palette.divider}`,
        bgcolor: alpha(theme.palette.warning.main, 0.08),
      }}
    >
      <Stack
        direction="row"
        spacing={0.75}
        sx={{ alignItems: "center", mb: 1 }}
      >
        <WarningAmberRounded sx={{ fontSize: 16, color: "warning.main" }} />
        <Typography sx={{ fontWeight: "bold" }} variant="body2">
          Metered ≠ billed
        </Typography>
      </Stack>
      <Typography variant="body2">
        {`billed ${cycle.billed_usage.toLocaleString()} ${unit} vs metered ${cycle.derived_usage.toLocaleString()} ${unit}`}
        {pct != null ? ` · Δ ${pct}%` : ""}
      </Typography>
      <Typography
        color="text.secondary"
        sx={{ display: "block", mt: 1 }}
        variant="dataLabel"
      >
        The metered total diverges from the billed usage for this cycle — check
        meter reads or bill-period alignment.
      </Typography>
    </Box>
  )
}

/**
 * Actual vs expected — CYCLE TOTALS. `expected`/`actual_dollars`/
 * `expected_dollars` are all already cycle-total figures (no per-day scaling
 * needed here, unlike the seasonal baseline's per-day rate). The $ line only
 * renders when a tariff resolved on both sides.
 */
const OverExpectedPanel: React.FC<{ cycle: AnomalousCycle }> = ({ cycle }) => {
  const theme = useTheme()
  const { formatCurrency } = useFormatCurrency({
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
  if (!cycle.over_expected) {
    return null
  }
  const unit = cycle.usage_unit
  const showDollars =
    cycle.actual_dollars != null && cycle.expected_dollars != null
  const dollarsOver = showDollars
    ? (cycle.dollars_over ??
      (cycle.actual_dollars as number) - (cycle.expected_dollars as number))
    : null

  return (
    <Box
      sx={{
        px: 3,
        py: 2,
        borderBottom: `1px solid ${theme.palette.divider}`,
        bgcolor: alpha(theme.palette.warning.main, 0.08),
      }}
    >
      <Stack
        direction="row"
        spacing={0.75}
        sx={{ alignItems: "center", mb: 1.5 }}
      >
        <WarningAmberRounded sx={{ fontSize: 16, color: "warning.main" }} />
        <Typography sx={{ fontWeight: "bold" }} variant="body2">
          Over expected
        </Typography>
      </Stack>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={3}>
          <Stat
            label="Actual"
            value={`${Math.round(cycle.usage).toLocaleString()} ${unit}`}
          />
          {cycle.expected != null ? (
            <Stat
              label="Expected"
              value={`${Math.round(cycle.expected).toLocaleString()} ${unit}`}
            />
          ) : null}
        </Stack>
        {showDollars ? (
          <Stack direction="row" spacing={3}>
            <Stat
              label="Actual $"
              value={formatCurrency(cycle.actual_dollars as number)}
            />
            <Stat
              label="Expected $"
              value={formatCurrency(cycle.expected_dollars as number)}
            />
            {dollarsOver != null ? (
              <Stat
                color="warning.main"
                label="Δ $"
                value={formatCurrency(dollarsOver)}
              />
            ) : null}
          </Stack>
        ) : null}
      </Stack>
      <Typography
        color="text.secondary"
        sx={{ display: "block", mt: 1.5 }}
        variant="dataLabel"
      >
        {`Observed total over the ${cycle.days}-day cycle — detection runs per day.`}
      </Typography>
    </Box>
  )
}

const BASIS_LABEL: Record<NonNullable<AnomalousCycle["basis"]>, string> = {
  seasonal: "vs typical this season",
  seasonal_window: "vs seasonal window",
  flat: "vs all cycles",
}

/**
 * Gas/water seasonal baseline. `baseline_median` is a PER-DAY rate (unlike
 * electric's cycle-total `expected`), so it's scaled by `days` to produce a
 * comparable cycle total. Renders only when the cycle carries a baseline tier.
 */
const SeasonalBaselinePanel: React.FC<{ cycle: AnomalousCycle }> = ({
  cycle,
}) => {
  const theme = useTheme()
  if (cycle.basis == null || cycle.baseline_median == null) {
    return null
  }
  const unit = cycle.usage_unit
  const typicalTotal = cycle.baseline_median * cycle.days

  return (
    <Box
      sx={{ px: 3, py: 2, borderBottom: `1px solid ${theme.palette.divider}` }}
    >
      <Typography sx={{ fontWeight: "bold", mb: 1 }} variant="body2">
        Seasonal baseline
      </Typography>
      <Stack direction="row" spacing={3}>
        <Stat
          label="This cycle"
          value={`${Math.round(cycle.usage).toLocaleString()} ${unit}`}
        />
        <Stat
          label="Typical"
          value={`${Math.round(typicalTotal).toLocaleString()} ${unit}`}
        />
      </Stack>
      <Typography
        color="text.secondary"
        sx={{ display: "block", mt: 1 }}
        variant="dataLabel"
      >
        {BASIS_LABEL[cycle.basis]}
      </Typography>
    </Box>
  )
}

export const BillDrawer: React.FC<BillDrawerProps> = ({
  month: cycle,
  open,
  onClose,
}) => {
  return (
    <Drawer anchor="right" onClose={onClose} open={open}>
      <Box
        sx={{
          width: 480,
          maxWidth: "100vw",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {open && cycle ? (
          <>
            <Box sx={{ p: 3, pb: 2 }}>
              <Stack
                direction="row"
                sx={{
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                }}
              >
                <Stack spacing={0.5}>
                  <Typography sx={{ fontWeight: "bold" }} variant="h3">
                    {formatPeriod(cycle.service_period)}
                  </Typography>
                  <Chip
                    icon={<ReceiptLongRounded />}
                    label={cycle.resource}
                    size="small"
                    sx={{ fontWeight: "bold", letterSpacing: "0.04em" }}
                    variant="outlined"
                  />
                </Stack>
                <IconButton aria-label="Close" onClick={onClose} size="small">
                  <CloseRounded />
                </IconButton>
              </Stack>
            </Box>

            <Divider />

            <MismatchPanel cycle={cycle} />
            <OverExpectedPanel cycle={cycle} />
            <SeasonalBaselinePanel cycle={cycle} />

            <DocumentPanel cycle={cycle} />
          </>
        ) : null}
      </Box>
    </Drawer>
  )
}
